// Ported from dev-scrips-pm-react src/documents/components/document-row.tsx (PROD-1119 documents module).
import { useState } from 'react';
import { ModalYesNo, DocumentRow as DsDocumentRow, type IconName } from '@scripsteam/scrips-signal-ds';
import { toast } from 'sonner';
import { useAuthStore } from '@/auth/auth-store';
import {
  useMarkDocumentReviewed,
  useArchiveDocument,
} from '@/documents/services/use-document-row-actions';
import { NewFilePopup } from '@/documents/components/new-file-popup';
import { DocumentType, type PatientDocumentModel } from '@/documents/types/document';
import docTypePdf from '@/assets/images/doc-type-pdf.png';

/**
 * DocumentRow — single row in the Docs tab list: a THIN data/mutation container
 * over the DS `DocumentRow` organism (PROD-1588 wave 4 L7 — the wave-3 B4
 * graduation of this row's anatomy onto the E1 docs frame 613:1925; the
 * hand-built card markup, popover menu and thumbnail retired in the same
 * motion). The DS row owns the anatomy + the More overflow menu; this file
 * owns everything app-side:
 *
 *   - **View** — opens the first non-deleted attached file in a new tab
 *     (browser-native PDF/image render; multi-file viewer is a follow-up).
 *   - **Mark Reviewed** — sets `reviewedById`; server fills name + date. The
 *     DS row hides the action once `reviewedBy` is present (the provenance
 *     line is the signal).
 *   - **Edit** — opens NewFilePopup seeded with the document (PROD-899).
 *   - **Archive / Unarchive** — soft-delete toggle (PROD-900). Archive keeps
 *     the host ModalYesNo confirm; Unarchive dispatches immediately
 *     (recoverable).
 *
 * Wire-shape contracts for the mutations live in `use-document-row-actions.ts`
 * (both reuse `POST /AddUpdatePatientDocument` with field overlays).
 */

interface DocumentRowProps {
  document: PatientDocumentModel;
  /**
   * WHICH SURFACE IS MOUNTING THIS ROW — the two draw different anatomies, and the split is
   * doctrinal, not a preference (2026-09-09, Ahmed: "make the PA reskin Docs tab like this",
   * against a capture of the deployed encounter Docs step).
   *
   * `chart` (default) — the DS `DocumentRow` as graduated: 15px bold brand-depth title, a
   * separator hairline, no card. Its anchor is **E1 `613:1925` (appointments@patient@docs)**,
   * i.e. the appointment/chart docs surface, and it is `status: "frozen"` in
   * FREEZE-MANIFEST.json. Untouched here.
   *
   * `encounter-step` — the encounter Docs STEP, which has **no Longevity frame**, so R9 puts
   * the Flutter widget source at rank 1. `document_list_widget.dart:65-136` draws a white
   * radius-13 card with a 2px brand-primary highlighter down its left edge, a 13px/500
   * brand-primary title and NO "n files attached" line. The geometry used to land in the PA's
   * `encounter-design-overrides.css` §26 (deleted, PA-Z 2026-09-23), hung off the classes this
   * variant adds; the two
   * prop-level differences (no `filesLine`, the PDF badge below) are made here.
   */
  surface?: 'chart' | 'encounter-step';
}

/** Document type → DS Icon registry name for the 56px type tile. Labs + Radiology ride
 *  the TRUE Scrips font glyphs since DS 1.52 bridged `lab` / `radiology` into SCRIPS_GLYPH
 *  (cure wave 5) — the generic-file fallback for those types is retired. */
function typeIconFor(type: string | null | undefined): IconName {
  switch (type) {
    case DocumentType.Forms:
      return 'clipboard';
    case DocumentType.Procedure:
      return 'doctor'; // Stethoscope — the same glyph the pre-swap row used
    case DocumentType.AdminReferrals:
      return 'folder';
    case DocumentType.Labs:
      return 'lab'; // Scrips font glyph (DS 1.52 SCRIPS_GLYPH bridge)
    case DocumentType.Radiology:
      return 'radiology'; // Scrips font glyph (DS 1.52 SCRIPS_GLYPH bridge)
    case DocumentType.Notes:
    default:
      return 'file';
  }
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

/** Date + time, e.g. "Jun 09, 2026 5:06p" — matches Flutter's
 *  "Updated by … on …" line. */
function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const date = d.toLocaleDateString('en-US', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
  const time = d
    .toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
    .replace(/\s?(AM|PM)$/i, (_m, p: string) => p.charAt(0).toLowerCase());
  return `${date} ${time}`;
}

function displayTitle(doc: PatientDocumentModel): string {
  // Flutter `title` getter: prefer `data.title` (used by Procedure /
  // Radiology types), fall back to `name`.
  return doc.data?.title || doc.name || 'Untitled document';
}

function firstVisibleFile(doc: PatientDocumentModel) {
  return (doc.patientDocumentFiles ?? []).find((f) => f && !f.isDeleted);
}

function isImageFile(file: { fileName?: string | null; fileType?: string | null } | undefined): boolean {
  if (!file) return false;
  const name = (file.fileName ?? '').toLowerCase();
  const ext = (file.fileType ?? '').toLowerCase();
  return /\.(jpe?g|png|gif|webp)$/.test(name) || /(jpe?g|png|gif|webp)/.test(ext);
}

/** `IconHelper.getIconByFileType` (icon_helper.dart:137-144) routes .pdf AND .rtf to the same
 *  `ic_pdf` raster — the red badge on the deployed row. */
function isPdfFile(file: { fileName?: string | null; fileType?: string | null } | undefined): boolean {
  if (!file) return false;
  const name = (file.fileName ?? '').toLowerCase();
  const ext = (file.fileType ?? '').toLowerCase();
  return /\.(pdf|rtf)$/.test(name) || /(pdf|rtf)/.test(ext);
}

export function PatientDocumentRow({ document, surface = 'chart' }: DocumentRowProps) {
  const encounterStep = surface === 'encounter-step';
  const userId = useAuthStore((s) => s.user?.userId);
  const markReviewed = useMarkDocumentReviewed();
  const archive = useArchiveDocument();

  const [confirmArchiveOpen, setConfirmArchiveOpen] = useState(false);
  // PROD-899: Edit popup is rendered per-row so each row owns its own
  // closed-state. Sheet primitives lazy-render their portal content, so
  // the cost when closed is just one boolean state hook per row.
  const [editOpen, setEditOpen] = useState(false);

  const fileCount = (document.patientDocumentFiles ?? []).filter(
    (f) => !f?.isDeleted,
  ).length;
  const isReviewed = !!document.reviewedOn;
  // PROD-900: `isDeleted` is the persisted archive flag — the DS row dims and
  // flips its menu between Archive and Unarchive off `archived`.
  const isArchived = !!document.isDeleted;
  const visibleFile = firstVisibleFile(document);
  const isPdf = isPdfFile(visibleFile);
  const actionPending = markReviewed.isPending || archive.isPending;

  const onView = () => {
    if (!visibleFile?.url) {
      toast.error('No file attached to view.');
      return;
    }
    // window.open with noopener+noreferrer (security best practice for
    // cross-origin link opening). The browser handles PDF / image rendering
    // natively in the new tab (the v1 new-tab decision, I14).
    window.open(visibleFile.url, '_blank', 'noopener,noreferrer');
  };

  const onMarkReviewed = async () => {
    if (!userId) {
      toast.error('Sign in again to mark as reviewed.');
      return;
    }
    try {
      await markReviewed.mutateAsync({ document, reviewerId: userId });
      toast.success('Marked as reviewed');
    } catch {
      /* Global toast surfaces failure; mutation cache invalidates row regardless. */
    }
  };

  const onArchiveConfirmed = async () => {
    setConfirmArchiveOpen(false);
    try {
      await archive.mutateAsync({ document, to: 'archived' });
      toast.success('Document archived');
    } catch {
      /* Global toast surfaces failure. */
    }
  };

  // PROD-900: Unarchive is recoverable (re-archive available), so skip the
  // confirm and dispatch immediately. Only the destructive direction
  // (Archive) gates behind a confirmation.
  const onUnarchive = async () => {
    try {
      await archive.mutateAsync({ document, to: 'active' });
      toast.success('Document unarchived');
    } catch {
      /* Global toast surfaces failure. */
    }
  };

  return (
    <>
      <DsDocumentRow
        title={displayTitle(document)}
        // "Updated by … on …" rides the meta line (Flutter document_list_widget parity).
        meta={`Updated by ${
          document.updatedByName?.trim() || document.createdByName?.trim() || '—'
        } on ${formatDateTime(document.updatedOn ?? document.createdOn ?? document.documentDate)}`}
        reviewedBy={
          isReviewed
            ? {
                name: document.reviewedByName?.trim() || '—',
                when: `on ${formatDate(document.reviewedOn)}`,
              }
            : undefined
        }
        className={
          encounterStep
            ? `pa-doc-row${isPdf ? ' pa-doc-row--badge' : ''}`
            : undefined
        }
        thumbnailUrl={
          isImageFile(visibleFile)
            ? (visibleFile?.url ?? undefined)
            : /* The deployed row does not draw an outline glyph for a PDF — `IconHelper` puts the
                 `ic_pdf` RASTER in the 56px tile at 24x24 (icon_helper.dart:137-144). The DS row
                 exposes no badge prop, so the asset rides the thumbnail slot and §26 sizes it to
                 24x24 `object-fit: contain` (the slot's own `object-cover` would crop a 72px
                 3x asset to a full-bleed square). The file is Flutter's own
                 `scrips_core/assets/3.0x/ic_pdf.png`, copied byte-identical (md5
                 857787bb66ce3baf90838d4236495b38), the same provenance rule the patient
                 placeholder avatar follows.
                 DS ASK: `DocumentRow` should take a per-file-type badge (or a `typeBadge` slot);
                 then this asset and the §26 sizing rule both go away. */
              encounterStep && isPdf
              ? docTypePdf
              : undefined
        }
        /* The encounter step draws Flutter's `highlighterCard`; Chart ▸ Docs and the appointment
           panel keep the E1 separator row. That used to be four CSS rules reaching into this
           component's internals (§28); Signal DS `c8a82f3` made it a seam, so it is a prop now.
           Two values move by 1px in the process, deliberately: the card radius resolves to
           `--radius-lg` (12px) where §28 hardcoded 13px, and the 2px rule's radius is
           `--radius-full` where §28 hardcoded 3px — visually identical on a 2px bar. 13px is on
           no spacing scale, and the same "snap an off-scale value to its token" call was already
           made for the questionnaire card's padding. */
        variant={encounterStep ? 'card' : 'separated'}
        /* The `ic_pdf` BADGE raster has to sit at its own 24x24 inside the 56px tile; the slot's
           default `object-cover` would crop a 72px 3x asset to a full-bleed square. This is the
           `DS ASK` recorded below, half-answered: the DS now exposes the FIT, though still not a
           per-file-type badge slot, so the asset still rides `thumbnailUrl`. */
        thumbnailFit={encounterStep && isPdf ? 'contain' : 'cover'}
        typeIcon={typeIconFor(document.type)}
        /* NOT on the encounter step: `document_list_widget.dart:104-127` renders title →
           optional "Reviewed by" → "Updated by … on …" and nothing else. The count line is
           the Chart/appointment row's own affordance and stays there. */
        filesLine={
          !encounterStep && fileCount > 0
            ? `${fileCount} file${fileCount === 1 ? '' : 's'} attached`
            : undefined
        }
        archived={isArchived}
        disabled={actionPending}
        onView={onView}
        onMarkReviewed={onMarkReviewed}
        onEdit={() => setEditOpen(true)}
        onArchive={() => setConfirmArchiveOpen(true)}
        onUnarchive={onUnarchive}
      />

      <ModalYesNo
        open={confirmArchiveOpen}
        title="Archive this document?"
        message={
          <>
            Archiving hides "{displayTitle(document)}" from the patient's
            active documents list. It stays in the audit log and can be
            restored from the archive view.
          </>
        }
        confirmLabel="Archive"
        cancelLabel="Cancel"
        destructive
        loading={archive.isPending}
        onConfirm={onArchiveConfirmed}
        onCancel={() => setConfirmArchiveOpen(false)}
      />

      {/* PROD-899: edit popup. Reuses NewFilePopup with the existing
          document as `initialDocument`. The popup hydrates state on
          open, saves via the same upsert endpoint (id non-null → server
          re-saves), and invalidates the patient-documents cache on
          success so this row's parent list refreshes. */}
      <NewFilePopup
        open={editOpen}
        onOpenChange={setEditOpen}
        patientId={document.patientId ?? ''}
        encounterId={document.encounterId ?? null}
        initialDocument={document}
      />
    </>
  );
}
